import { convertHtmlStringToElements } from "../../../js/utils/helper/helper";

class SearchResults {
  /**
   * takes care of the lazy loading of search results
   *
   * @param {HTMLElement} element - SearchResults element
   */
  constructor(element) {
    this.element = element;

    this.config = {
      selectors: {
        search: ".js-Search",
        items: ".js-SearchResults-items",
        loading: ".js-SearchResults-loading",
      },
      classes: {
        hidden: "is-hidden",
      },
    };

    this.itemsEl = this.element.querySelector(this.config.selectors.items);
    this.loadingEl = this.element.querySelector(this.config.selectors.loading);
    this.searchEl = document.querySelector(this.config.selectors.search);

    this.endpointUrl = this.element.getAttribute("data-endpoint-url");

    this.queryString = "";
    this.page = 1;
    this.isEnd = false;

    this._onLoadingIntersecting = this._onLoadingIntersecting.bind(this);

    this._init();
  }

  /**
   * init
   */
  _init() {
    if (this.endpointUrl) {
      this._hideLoading(false);
      this._initIntersectionObserver();
    }
  }

  /**
   * inits intersection observer
   */
  _initIntersectionObserver() {
    const options = {
      rootMargin: this.searchEl ? "0px 0px 300px 0px" : "0px 0px 100px 0px",
      threshold: 1.0,
    };

    const observer = new IntersectionObserver(
      this._onLoadingIntersecting,
      options
    );

    observer.observe(this.loadingEl);
  }

  /**
   * handles the intersection of the loading element
   *
   * @param { IntersectionObserverEntry[] } entries
   */
  _onLoadingIntersecting(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting && !this.isEnd) {
        this._prepareRequest();
      }
    });
  }

  /**
   * collects data from search form and prepares request url
   */
  async _prepareRequest() {
    const data = this.searchEl
      ? new FormData(this.searchEl)
      : new URL(this.endpointUrl).search;

    this.queryString = new URLSearchParams(data).toString();

    const endpointUrl = this._getUrlWithQueryParams(this.endpointUrl);
    await this._fetchContent(endpointUrl);
  }

  /**
   * sends request to the api endpoint
   *
   * @param {string} url - api endpoint
   */
  async _fetchContent(url) {
    try {
      this._setAriaBusy(true);

      const response = await fetch(url, {
        method: "GET",
      });

      const content = await response.json();

      if (content && content.data) {
        this._handleContentAvailable(content.data);
      } else {
        this.isEnd = true;
        this._hideLoading(true);
      }
      this._setAriaBusy(false);
    } catch (err) {
      this._setAriaBusy(false);
    }
  }

  /**
   * adds content to items
   *
   * @param {string} content - represents html markup
   */
  _handleContentAvailable(content) {
    const elements = convertHtmlStringToElements(content);
    this.itemsEl.append(elements);

    const locationUrl = this._getUrlWithQueryParams(window.location.href);
    SearchResults._pushHistory(locationUrl);

    this.page += 1;
  }

  /**
   * toggles hidden class for loading animation
   *
   * @param {boolean} state
   */
  _hideLoading(state) {
    this.loadingEl.classList.toggle(this.config.classes.hidden, state);
  }

  /**
   * returns an url with the current form values as query params
   *
   * @param {string} url
   * @returns {string} url
   */
  _getUrlWithQueryParams(url) {
    const urlObj = new URL(url);
    urlObj.search = `${this.queryString}&page=${this.page}`;

    return urlObj.toString();
  }

  /**
   * sets aria-busy attribute to items
   *
   * @param {boolean} state
   */
  _setAriaBusy(state) {
    this.itemsEl.setAttribute("aria-busy", state);
  }

  /**
   * pushes a new state to the browser history
   *
   * @param {string} url
   */
  static _pushHistory(url) {
    window.history.pushState({}, "", url);
  }
}

export default SearchResults;
